import { NavLink } from 'react-router-dom';

import { array, dom } from 'app/utils';
import { fetchLookup, getLookup, store } from 'app/state';
import { utils } from 'app/state/lookup';
import { ICountry, ILookup, IProfile, IStamp } from 'app/models';
import { MenuItem, MenuItemOptions, Ripple } from '../external';
import { Button, IButton, Icon, Label, ILabel } from '../common';
import { ISource } from '../types';

export const menu = (item: MenuItem, options: MenuItemOptions) => {
  const props: any = {
    className: dom.className(options.className, { 'active': (!item.url && item.data?.active) }),
    tabIndex: -1,
    'aria-hidden': true,
    'aria-haspopup': !!item.items?.length,
    'data-pc-section': 'action',

    onFocus: (e: FocusEvent) => e.stopPropagation(),
    onClick: options.onClick,

    children: (<>
      { item.icon && <Icon name={item.icon} className={dom.className(options.iconClassName, item.className, 'mr-3')} /> }
      <span className={dom.className(options.labelClassName, item.className)} data-pc-section="label">{item.label}</span>
      { !!item.items?.length && <i className="p-icon p-submenu-icon pi pi-angle-right" /> }
      <Ripple />
    </>),
  };

  return (item.url ? <NavLink to={item.url} { ...props } /> : <span { ...props } />);
};

export const popup = (item: MenuItem, options: MenuItemOptions) => (
  <div className="p-menuitem-content" data-pc-section="content">
    { menu(item, options) }
  </div>
);

export const profile = (profile?: IProfile) => {
  if (!profile) return;

  const button: IButton = { text: true, href: `/profiles/${profile.id}`, className: 'px-3 py-2 i-inline',
    icon: (profile.type === 2 ? 'mdi-business' : 'mdi-account-circle'),
    label: `${profile.firstName} ${profile.lastName}`,
  };

  return <Button { ...button } />;
};

export const country = (country?: ICountry) => {
  if (!country) return;

  const label: ILabel = { className: 'px-3 py-2 inline-flex align-items-center',
    icon: 'mdi-flag',
    text: country.name,
  };

  return <Label { ...label } />;
};

const plans = new Map<string, any>();
plans.set('business', { icon: 'mdi-domain' });
plans.set('free', { icon: 'mdi-person' });
plans.set('star', { icon: 'mdi-star' });
plans.set('superstar', { icon: 'mdi-star' });
plans.set('megastar', { icon: 'mdi-star' });

export const membership = (stamp?: IStamp) => {
  if (!stamp) return;

  const label: ILabel = { ...plans.get(stamp.code!), className: 'px-3 py-2 inline-flex align-items-center',
    text: stamp.name,
  };

  return <Label { ...label } />;
};

const memory = new Map<string, Promise<any>>();

export const source = <T extends ILookup = ILookup>(source: ISource<T>, value: any, update?: () => void) => {
  const sources: Record<ISource<T>['type'], ((source: ISource<T>) => T[] | undefined)> = {
    'lookup': ({ key, params }) => (key ? getLookup(store.getState(), utils.encode(key, params)) as T[] : undefined),
    'static': ({ data }) => data,
  };

  const { type, key, params } = source;
  const data = sources[type]?.(source);

  if (!data && type === 'lookup' && key) {
    const tag = utils.encode(key, params);
    if (!memory.has(tag)) {
      const promise = store.dispatch(fetchLookup({ key, params }));
      memory.set(tag, promise);

      if (update) promise.then(() => update());
    }
  }

  return array.collect<T>(data ?? [], (o) => o.value)[value];
};
